import React from 'react';
import styled, { css } from 'styled-components';
import { theme } from '../../theme';
import { Spinner } from '../Spinner';
import { Box } from '../Box';
import { tabNavigationStyle } from '../../helpers/tabNavigationStyle';
const iconStyle = () => `
  width: auto;
  display: flex;
  align-items: center;
  svg {
    path {
      transition: fill 200ms ease-in-out;
    }
  }
`;
const iconColored = (color) => `
  svg {
    path {
      fill: ${theme.colors[color]};
    }
  }
`;
const iconWhite = () => `
  svg {
    path {
      fill: ${theme.colors.white};
    }
  }
`;
const outlinedHoverStyle = (color) => `
  box-shadow: ${theme.shadows.gray32};
  color: ${theme.colors.white};
  background-color: ${theme.colors[color]};
  ${iconWhite()}
`;
const outlinedActiveStyle = (color) => `
  background-color: ${theme.colorShade({
    type: 'pressed',
    color: color,
})};
  border: 2px solid ${theme.colorShade({
    type: 'pressed',
    color: color,
})};
  color: ${theme.colors.white};
  ${iconWhite()}
`;
const outlinedStyle = (color, isWrapper) => `
  background-color: ${theme.colors.transparent};
  border: ${isWrapper ? 'none' : `2px solid ${theme.colors[color]}`};
  color: ${theme.colors[color]};

  ${isWrapper
    ? `button {
        ${iconColored(color)}
      }`
    : iconColored(color)}

  &:hover,
  &:focus {
    ${isWrapper
    ? `button {
          ${outlinedHoverStyle(color)}
        }`
    : outlinedHoverStyle(color)}
  }

  &:active {
    ${isWrapper
    ? `button {
          ${outlinedActiveStyle(color)}
        }`
    : outlinedActiveStyle(color)}
  }
`;
const ghostStyle = (color) => `
  background-color: transparent;
  border: 2px solid ${theme.colors.white};
  color: ${theme.colors.white};

  ${iconWhite()}

  &:hover,
  &:focus {
    background-color: ${theme.colors[color]};
    border-color: ${theme.colors[color]};
  }

  &:active {
    background-color: ${theme.colorShade({ type: 'pressed', color: color })};
    color: ${theme.colors.white};
    border-color: ${theme.colors[color]};

    ${iconWhite()}
  }
`;
const linkStyle = (color, isDisabled) => `
  background-color: ${theme.colors.transparent};
  border: none;
  color: ${isDisabled ? theme.colors.gray700 : theme.colors[color]};

  svg {
    path {
      fill: ${isDisabled ? theme.colors.gray700 : theme.colors[color]};
    }
  }

  ${!isDisabled
    ? `
    &:hover,
    &:focus{
      color: ${theme.colorShade({ type: 'hover', color: color })};
      text-decoration: underline;
      svg {
        path {
          fill: ${theme.colorShade({
        type: 'hover',
        color: color,
    })};
        }
      }
    }
  `
    : ''}
`;
const disabledStyle = css `
  background-color: ${theme.colors.gray200};
  border: 2px solid ${theme.colors.gray200};
  color: ${theme.colors.gray700};
  svg {
    path {
      fill: ${theme.colors.gray700};
    }
  }
`;
const mainHoverStyle = (color) => `
  box-shadow: ${theme.shadows.gray32};
  background-color: ${theme.colorShade({
    type: 'hover',
    color: color,
})};
  border: 2px solid ${theme.colorShade({
    type: 'hover',
    color: color,
})};
`;
const mainActiveStyle = (color) => `
  background-color: ${theme.colorShade({
    type: 'pressed',
    color: color,
})};
  border: 2px solid ${theme.colorShade({ type: 'pressed', color: color })};
`;
const mainStyle = (color, isWrapper) => `
  &:hover,
  &:focus {
    ${isWrapper
    ? `button {
          ${mainHoverStyle(color)}
        }`
    : mainHoverStyle(color)}
  }
  &:active {
    ${isWrapper
    ? `button {
          ${mainActiveStyle(color)}
        }`
    : mainActiveStyle(color)}
  }
`;
const Component = styled.button `
  ${({ theme, isBlock, size, icon, isDisabled, isWrapper }) => `
    position: relative;
    border-radius: 8px;
    justify-content: center;
    font-family: ${theme.fonts.body};
    font-size: ${theme.fontSizes.x1};
    font-weight: ${theme.fontWeights.bold};
    cursor: ${isDisabled ? 'not-allowed' : 'pointer'};
    line-height: 1;
    text-transform: uppercase;
    transition: all 0.2s ease-in-out;
    padding: ${isWrapper
    ? theme.space.x0
    : size === 'small'
        ? theme.space.x8
        : theme.space.x16};
    ${icon ? iconStyle() : ''}
    width: ${isBlock ? '100%' : size === 'small' ? 'auto' : '10rem'};
    ${isWrapper
    ? `
          display: flex;
          justify-content: center;
          align-items: center;
          min-width: 48px;
          min-height: 48px;
    `
    : ''}
  `}

  ${({ isDisabled, isOutlined, isLoading, ghost, color = 'primary', fontColor, isLink, theme, isWrapper, }) => {
    if (isLink) {
        return linkStyle(color, !!isDisabled);
    }
    if (isOutlined) {
        if (ghost) {
            return ghostStyle(color);
        }
        return outlinedStyle(color, isWrapper);
    }
    if (isDisabled) {
        return disabledStyle;
    }
    return css `
      background-color: ${isWrapper
        ? theme.colors.transparent
        : theme.colors[color]};
      border: ${isWrapper ? 'none' : `2px solid ${theme.colors[color]}`};
      color: ${fontColor ? theme.colors[fontColor] : theme.colors.white};
      ${isWrapper &&
        `
        min-width: 48px;
        min-height: 48px;
      `}

      &:disabled {
        cursor: not-allowed;
      }

      ${iconWhite()}

      ${!isLoading && mainStyle(color, isWrapper)}
    `;
}}
  ${tabNavigationStyle};
`;
export const Button = ({ children, htmlAttrs = {}, color, fontColor, isLink, isDisabled, isBlock, isLoading, isOutlined, ghost, ariaLabel, role, onClick, size, icon, ...rest }) => (React.createElement(Component, { ...htmlAttrs, color: color, fontColor: fontColor, isLink: isLink, icon: icon, isLoading: isLoading, isDisabled: isDisabled, isBlock: isBlock, isOutlined: isOutlined, ghost: ghost, onClick: onClick, disabled: isDisabled || isLoading, "aria-label": ariaLabel, role: role, size: size, ...rest },
    icon && (React.createElement(Box, { marginRight: children ? 'x8' : 'x0', display: "flex" }, icon)),
    children,
    isLoading && (React.createElement(Spinner, { bottom: `calc(2px + ${theme.fontSizes.x1})`, right: `calc(2px + ${theme.space.x8})` }))));
Button.defaultProps = {
    color: 'success',
    isDisabled: false,
    isBlock: false,
    isLoading: false,
    isOutlined: false,
    ghost: false,
    size: 'large',
};
//# sourceMappingURL=index.js.map